/**
 * "Still ingesting" status shown for the full duration of an in-flight
 * ingestion run.
 *
 * The ingestion pipeline is a single synchronous backend call
 * (`POST /api/ingestion/runs`, see `useSources`) with no intermediate
 * step/progress signal to poll, so a real determinate step indicator isn't
 * available. This renders an always-animating (pure CSS) spinner plus a real,
 * ticking elapsed-time counter instead of a fake staged progress bar or —
 * worse — a frozen line: a live ingest can legitimately take minutes, not the
 * "30-60s" this used to (inaccurately) promise.
 *
 * The elapsed counter and spinner are marked `aria-hidden` and kept OUTSIDE
 * the `role="status"` text so a screen reader announces the status once (its
 * text never changes) rather than every second as the counter ticks.
 */
import React, { useEffect, useState } from "react";
import { formatElapsed } from "./formatElapsed";

/** Props for {@link IngestProgress}. */
interface IngestProgressProps {
  /** `data-testid` applied to the status container. */
  testId: string;
  /** Extra classes merged onto the container (layout only). */
  className?: string;
  /**
   * The status text (defaults to the ingestion copy). Callers with a
   * different long-running, no-progress-signal operation (e.g. the
   * populate-sources call) pass their own honest copy instead of duplicating
   * this whole spinner+elapsed-counter component.
   */
  message?: string;
}

/**
 * Render an accessible, continuously-animating "still working" status.
 *
 * @param props - The test id, optional layout classes, and optional message.
 * @returns The status element.
 */
export const IngestProgress: React.FC<IngestProgressProps> = ({
  testId,
  className = "",
  message = "Ingesting… this can take a few minutes.",
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-2 text-xs text-amber-700 ${className}`}
    >
      <span
        aria-hidden="true"
        data-testid={`${testId}-spinner`}
        className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-amber-300 border-t-amber-700"
      />
      <span role="status" aria-busy="true">
        {message}
      </span>
      <span aria-hidden="true" data-testid={`${testId}-elapsed`}>
        {formatElapsed(elapsedSeconds)} elapsed
      </span>
    </div>
  );
};

export default IngestProgress;
